import { buffers, eventChannel, SagaIterator } from 'redux-saga'
import {
  all,
  call,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest
} from 'redux-saga/effects'
import { KANBAN, TYPES, CardKanban, MensagemChatwootDto } from './types'
import * as kanbanController from 'controller/kanbanController'
import * as atendimentoController from 'controller/atendimentoController'
import * as kanbanActions from 'store/modules/kanban/actions'
import * as loadingActions from 'store/modules/loading/actions'
import { TYPES as ATENDIMENTO_TYPES } from 'store/modules/atendimento/types'
import { StatusChamadaEnum } from 'models/atendimento/types'
import { handlerError } from 'util/handlerError'
import { ApplicationState } from 'store'
import { Action } from 'redux'
import {
  maskCPF,
  maskPhoneWithDDD,
  removeMaskCPF,
  removeMaskMoney
} from 'util/masks'
import { NotificacaoKanbanDto, PipelineResponse } from 'models/kanban/types'
import { toast } from 'react-toastify'
import { HubConnection, HubConnectionState } from '@microsoft/signalr'

interface GetKanbanProps {
  pipelineId?: string
}

function* getKanban({
  loading = true,
  pipelineId
}: LoadingProps & GetKanbanProps = {}): SagaIterator {
  const { filtros }: KANBAN = yield select(
    (state: ApplicationState) => state.kanban
  )
  try {
    if (loading) yield put(kanbanActions.setLoadingKanban(true))
    const response = yield call(
      kanbanController.getKanbanPipeline,
      filtros.pipelineId || pipelineId || '',
      filtros.isArchived
    )
    const kanban: KANBAN['kanbanData'] = {
      nome: response?.data?.content.nome || '',
      descricao: response?.data?.content.descricao || '',
      pipelineId: response?.data?.content.pipelineId || '',
      centroCustoId: response?.data?.content.centroCustoId || '',
      site: response?.data?.content.site || false,
      columns: response?.data?.content.etapas.map(
        (etapa: PipelineResponse['etapas'][number]) => {
          return {
            id: etapa.id,
            title: etapa.nome,
            cards: etapa.clientesEtapas.map((cliente) => {
              return {
                id: cliente.pipelineEtapaClienteId,
                clienteId: cliente.clienteId,
                cpf: cliente.cpf,
                title: cliente.nome,
                telefone:
                  cliente.ddd && cliente.numero
                    ? maskPhoneWithDDD(cliente.ddd + cliente.numero)
                    : '',
                email: cliente.email || '',
                value: cliente.valorOportunidade || 0,
                time: cliente.dataMovimentacao
                  ? cliente.dataMovimentacao
                  : cliente.dataCriacao,
                borderColor: etapa.cor,
                perfis: cliente.perfis || [],
                nomeConsultor: cliente.nomeConsultor || null,
                consultorId: cliente.consultorId || null,
                situacao: cliente.situacao
              }
            }),
            etapasPermitidas: etapa.etapasPermitidas,
            modalMotivo: etapa.modalMotivo
          }
        }
      )
    }
    yield put(kanbanActions.setKanban(kanban))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error?.message === 'Pipeline não encontrada') {
      yield put(
        kanbanActions.setKanban({
          nome: '',
          descricao: '',
          centroCustoId: '',
          pipelineId: '',
          site: false,
          columns: []
        })
      )
      yield put(
        kanbanActions.setBigNumbers({
          totalLeads: 0,
          totalLeadsAtraso: 0,
          totalNovosLeads: 0,
          totalTarefasConsultorHoje: 0
        })
      )
      yield put(kanbanActions.setFiltrosField(null, 'pipelineId'))
      yield put(kanbanActions.verificaHierarquiaEDisparaGetsSaga())
    }
    handlerError(error)
  } finally {
    if (loading) yield put(kanbanActions.setLoadingKanban(false))
  }
}

function* getCentrosCusto(): SagaIterator {
  try {
    yield put(kanbanActions.setLoadingSelect(true, 'centrosCusto'))
    const response = yield call(kanbanController.getCentrosDeCusto)
    yield put(
      kanbanActions.setOptionsSelect(
        response?.data?.content || [],
        'centrosCusto'
      )
    )
  } catch (error) {
    yield put(kanbanActions.setOptionsSelect([], 'centrosCusto'))
    handlerError(error)
  } finally {
    yield put(kanbanActions.setLoadingSelect(false, 'centrosCusto'))
  }
}

interface GetPipelinesProps extends GenericIdProps {
  buscaPipelineDefault?: boolean
}

function* getPipelines({
  id,
  buscaPipelineDefault = true
}: GetPipelinesProps): SagaIterator {
  const { filtros }: KANBAN = yield select(
    (state: ApplicationState) => state.kanban
  )
  try {
    yield put(kanbanActions.setLoadingSelect(true, 'pipelines'))
    if (buscaPipelineDefault && !filtros.pipelineId) {
      try {
        const responsePipeline = yield call(kanbanController.getPipelineDefault)
        yield put(
          kanbanActions.setFiltrosField(
            responsePipeline?.data?.content.pipelineId || '',
            'pipelineId'
          )
        )
      } catch {
        //
      }
    }
    const response = yield call(kanbanController.getPipelines, id)
    yield put(
      kanbanActions.setOptionsSelect(response?.data?.content || [], 'pipelines')
    )
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    yield put(kanbanActions.setOptionsSelect([], 'pipelines'))
    handlerError(error)
  } finally {
    yield put(kanbanActions.setLoadingSelect(false, 'pipelines'))
  }
}

interface MovimentacaoCardKanbanProps extends Action {
  value: kanbanController.MovimentacaoCardsDto
}

interface LoadingProps {
  loading?: boolean
}

interface GenericIdProps extends Action {
  id: string
}

interface VerificarMotivoMovimentacaoProps extends Action {
  value: {
    card: CardKanban
    from: { fromPosition: number; fromColumnId: string }
    to: { toPosition: number; toColumnId: string }
  }
}

function* verificarMotivoMovimentacao({
  value
}: VerificarMotivoMovimentacaoProps): SagaIterator {
  const { kanbanData, motivoMovimentacao }: KANBAN = yield select(
    (state: ApplicationState) => state.kanban
  )

  try {
    const colunaDestino = kanbanData.columns.find(
      (col) => String(col.id) === String(value.to.toColumnId)
    )

    const requerMotivo = colunaDestino?.modalMotivo || false

    if (requerMotivo) {
      yield put(
        kanbanActions.setMovimentacaoPendente({
          card: value.card,
          from: value.from,
          to: value.to
        })
      )
      yield put(
        kanbanActions.setMotivoMovimentacao({
          ...motivoMovimentacao,
          pipelineEtapaClienteId: String(value.card.id)
        })
      )
      yield put(kanbanActions.setIsOpenModalMotivo(true))

      return true
    } else {
      yield put(
        kanbanActions.movimentarCardKanbanSaga({
          pipelineId: kanbanData.pipelineId,
          pipelineEtapaOrigemId: value.from.fromColumnId || '',
          pipelineEtapaDestinoId: value.to.toColumnId || '',
          pipelineEtapaClienteId: String(value.card.id) || '',
          clienteId: value.card.clienteId || '',
          ordem: value.to.toPosition
        })
      )

      return false
    }
  } catch (error) {
    handlerError(error)
    return false
  }
}

function* executarMovimentacaoComMotivo(): SagaIterator {
  const { movimentacaoPendente, kanbanData }: KANBAN = yield select(
    (state: ApplicationState) => state.kanban
  )

  if (!movimentacaoPendente) {
    console.warn('Nenhuma movimentação pendente encontrada')
    return
  }

  try {
    yield call(criarMotivoMovimentacao)
    yield put(
      kanbanActions.movimentarCardKanbanSaga({
        pipelineId: kanbanData.pipelineId,
        pipelineEtapaOrigemId: movimentacaoPendente.from.fromColumnId || '',
        pipelineEtapaDestinoId: movimentacaoPendente.to.toColumnId || '',
        pipelineEtapaClienteId: String(movimentacaoPendente.card.id) || '',
        clienteId: movimentacaoPendente.card.clienteId || '',
        ordem: movimentacaoPendente.to.toPosition
      })
    )

    yield put(kanbanActions.setMovimentacaoPendente(null))
    yield put(kanbanActions.setIsOpenModalMotivo(false))
    yield put(kanbanActions.cleanMotivoMovimentacao())
  } catch (error) {
    handlerError(error)
  }
}

function* criarMotivoMovimentacao(): SagaIterator {
  const { motivoMovimentacao, movimentacaoPendente }: KANBAN = yield select(
    (state: ApplicationState) => state.kanban
  )

  if (!movimentacaoPendente) {
    console.warn('Nenhuma movimentação pendente encontrada para criar motivo')
    return
  }

  try {
    yield put(loadingActions.setLoading(true))
    yield call(kanbanController.criarMotivoMovimentacao, {
      pipelineEtapaClienteId: motivoMovimentacao.pipelineEtapaClienteId || '',
      motivoId: motivoMovimentacao.motivoId,
      observacao: motivoMovimentacao.observacao
    })
    yield call(getInformacoesCardKanban, {
      id: motivoMovimentacao.pipelineEtapaClienteId,
      type: ''
    })
  } catch (error) {
    handlerError(error)
    throw error
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getMotivosPerda(): SagaIterator {
  try {
    yield put(kanbanActions.setLoadingSelect(true, 'motivosPerda'))
    const response = yield call(kanbanController.getMotivosPerda)
    yield put(
      kanbanActions.setOptionsSelect(
        response?.data?.content || [],
        'motivosPerda'
      )
    )
  } catch (error) {
    yield put(kanbanActions.setOptionsSelect([], 'motivosPerda'))
    handlerError(error)
  } finally {
    yield put(kanbanActions.setLoadingSelect(false, 'motivosPerda'))
  }
}

function* movimentarCardKanban({
  value
}: MovimentacaoCardKanbanProps): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(kanbanController.movimentarCardKanban, value)
    const payload = response?.data.content
    const etapaOrigem = payload?.etapaOrigem
    const etapaDestino = payload?.etapaDestino

    if (etapaOrigem || etapaDestino) {
      const { kanbanData }: KANBAN = yield select(
        (state: ApplicationState) => state.kanban
      )

      const mapEtapaToColumn = (etapa: PipelineResponse['etapas'][number]) => {
        const currentColumn = kanbanData.columns.find(
          (col) => String(col.id) === String(etapa.id)
        )

        return {
          id: etapa.id,
          title: etapa.nome,
          cards: (etapa.clientesEtapas || []).map((cliente) => ({
            id: cliente.pipelineEtapaClienteId,
            clienteId: cliente.clienteId,
            cpf: cliente.cpf,
            title: cliente.nome,
            telefone:
              cliente.ddd && cliente.numero
                ? maskPhoneWithDDD(cliente.ddd + cliente.numero)
                : '',
            email: cliente.email || '',
            value: cliente.valorOportunidade || 0,
            time: cliente.dataMovimentacao
              ? cliente.dataMovimentacao
              : cliente.dataCriacao,
            borderColor: etapa.cor,
            perfis: cliente.perfis || [],
            nomeConsultor: cliente.nomeConsultor || null,
            consultorId: cliente.consultorId || null,
            situacao: cliente.situacao
          })),
          etapasPermitidas:
            etapa.etapasPermitidas && etapa.etapasPermitidas.length > 0
              ? etapa.etapasPermitidas
              : currentColumn?.etapasPermitidas || [],
          modalMotivo: etapa.modalMotivo ?? currentColumn?.modalMotivo
        }
      }

      const mappedOrig = etapaOrigem ? mapEtapaToColumn(etapaOrigem) : null
      const mappedDest = etapaDestino ? mapEtapaToColumn(etapaDestino) : null

      let newKanban = kanbanData
      if (kanbanData && kanbanData.columns) {
        const newColumns = kanbanData.columns.map((col) => {
          if (mappedOrig && String(col.id) === String(mappedOrig.id))
            return mappedOrig
          if (mappedDest && String(col.id) === String(mappedDest.id))
            return mappedDest
          return col
        })
        newKanban = { ...kanbanData, columns: newColumns }
      }

      yield put(kanbanActions.setKanban(newKanban))
    }
  } catch (error) {
    handlerError(error)
    yield put(kanbanActions.getKanbanSaga())
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getInformacoesCardKanban({
  id,
  loading = true
}: GenericIdProps & LoadingProps): SagaIterator {
  try {
    if (loading) yield put(loadingActions.setLoading(true))
    const response = yield call(kanbanController.getInformacoesCardKanban, id)
    if (response?.data?.content) {
      yield put(kanbanActions.setInformacoesModalKanban(response?.data.content))
      yield put(kanbanActions.setIsOpenModalKanban(true))
      yield put(
        kanbanActions.setConsultorModalKanban({
          id: response?.data.content.consultorId,
          nome: response?.data.content.nomeConsultor
        })
      )
    }
  } catch (error) {
    handlerError(error)
  } finally {
    if (loading) yield put(loadingActions.setLoading(false))
  }
}

interface AtribuirConsultorCardKanbanProps extends Action {
  value: kanbanController.AtribuirConsultorDto
}
function* atribuirConsultorCardKanban({
  value
}: AtribuirConsultorCardKanbanProps): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    yield call(kanbanController.atribuirConsultorCardKanban, value)
    yield call(getInformacoesCardKanban, {
      id: value.pipelineEtapaClienteId,
      type: ''
    })
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getConsultoresModalKanban({ id }: GenericIdProps): SagaIterator {
  try {
    yield put(kanbanActions.setLoadingSelect(true, 'consultores'))
    const response = yield call(kanbanController.getConsultores, id)
    yield put(
      kanbanActions.setOptionsSelect(
        response?.data?.content || [],
        'consultores'
      )
    )
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(kanbanActions.setLoadingSelect(false, 'consultores'))
  }
}

function* criarAnotacaoCardKanban({ id }: GenericIdProps): SagaIterator {
  const { modalKanban }: KANBAN = yield select(
    (state: ApplicationState) => state.kanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    yield call(kanbanController.criarAnotacaoCardKanban, {
      pipelineEtapaClienteId: id,
      descricao: modalKanban.anotacao
    })
    yield put(kanbanActions.setAnotacaoCardKanban(''))
    yield call(getInformacoesCardKanban, {
      id,
      type: ''
    })
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getBigNumbers({ id }: GenericIdProps): SagaIterator {
  try {
    const response = yield call(kanbanController.getBigNumbers, id)
    if (response?.data?.content) {
      yield put(kanbanActions.setBigNumbers(response?.data?.content))
    }
  } catch (error) {
    handlerError(error)
  }
}

function* cadastrarCliente(): SagaIterator {
  const { modalCadastroCliente, kanbanData }: KANBAN = yield select(
    (state: ApplicationState) => state.kanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    const infosCliente: kanbanController.RegisterClienteDto = {
      pipelineId: kanbanData.pipelineId,
      convenioId: modalCadastroCliente.register.convenioId,
      nome: modalCadastroCliente.register.nome,
      cpf: removeMaskCPF(modalCadastroCliente.register.cpf),
      dataNascimento: modalCadastroCliente.register.dataNascimento,
      matricula: modalCadastroCliente.register.matricula,
      ddd: modalCadastroCliente.register.ddd,
      telefone: modalCadastroCliente.register.telefone,
      perfilId: modalCadastroCliente.register.perfilId || '',
      margem: Number(removeMaskMoney(modalCadastroCliente.register.margem)),
      margemCartao: Number(
        removeMaskMoney(modalCadastroCliente.register.margemCartao)
      ),
      numeroNit: Number(modalCadastroCliente.register.numeroNit)
    }
    yield call(kanbanController.cadastrarCliente, infosCliente)
    yield put(kanbanActions.cleanRegisterModalCadastroCliente())
    yield put(kanbanActions.setIsOpenModalCadastroCliente(false))
    yield put(kanbanActions.getKanbanSaga())
    yield put(kanbanActions.getBigNumbersSaga(kanbanData.pipelineId))
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

interface GenericAction extends Action {
  cpf: string
}
function* adicionarClienteKanban({ cpf }: GenericAction): SagaIterator {
  const { kanbanData }: KANBAN = yield select(
    (state: ApplicationState) => state.kanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    const cpfFormatted = removeMaskCPF(cpf)
    yield call(kanbanController.adicionarClienteKanban, {
      cpf: cpfFormatted,
      pipelineId: kanbanData.pipelineId
    })

    yield put(kanbanActions.setIsOpenModalCpf(false))
    yield put(kanbanActions.setCpfModalCpf(''))
    yield put(kanbanActions.getKanbanSaga())
    yield put(kanbanActions.getBigNumbersSaga(kanbanData.pipelineId))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.log('error', error)
    if (error?.message === 'Pessoa não encontrada.') {
      yield put(kanbanActions.setIsOpenModalConfirmacao(true))
    } else {
      handlerError(error)
    }
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getConvenios(): SagaIterator {
  try {
    yield put(kanbanActions.setLoadingSelect(true, 'convenios'))
    const response = yield call(kanbanController.getConvenios)
    yield put(
      kanbanActions.setOptionsSelect(response?.data?.content || [], 'convenios')
    )
  } catch (error) {
    yield put(kanbanActions.setOptionsSelect([], 'convenios'))
    handlerError(error)
  } finally {
    yield put(kanbanActions.setLoadingSelect(false, 'convenios'))
  }
}

function* getPerfis(): SagaIterator {
  try {
    yield put(kanbanActions.setLoadingSelect(true, 'perfis'))
    const response = yield call(kanbanController.getPerfis)
    yield put(
      kanbanActions.setOptionsSelect(response?.data?.content || [], 'perfis')
    )
  } catch (error) {
    yield put(kanbanActions.setOptionsSelect([], 'perfis'))
    handlerError(error)
  } finally {
    yield put(kanbanActions.setLoadingSelect(false, 'perfis'))
  }
}

function* getPipelineDefault(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(kanbanController.getPipelineDefault)
    const defaultPipelineId = response?.data?.content?.pipelineId || ''
    yield put(kanbanActions.setFiltrosField(defaultPipelineId, 'pipelineId'))
    yield call(getKanban)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    yield put(
      kanbanActions.setKanban({
        nome: '',
        descricao: '',
        centroCustoId: '',
        pipelineId: '',
        site: false,
        columns: []
      })
    )
    yield put(
      kanbanActions.setBigNumbers({
        totalLeads: 0,
        totalLeadsAtraso: 0,
        totalNovosLeads: 0,
        totalTarefasConsultorHoje: 0
      })
    )
    yield put(kanbanActions.setFiltrosField(null, 'pipelineId'))
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* verificaHierarquiaEDisparaGets(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(kanbanController.getHierarquia)
    const isCoordenador = response?.data?.content.coordenador || false
    yield put(kanbanActions.setCoordenador(isCoordenador))
    if (isCoordenador) {
      const {
        filtros: filtrosAnteriores,
        kanbanData: kanbanDataAnterior
      }: KANBAN = yield select((state: ApplicationState) => state.kanban)
      const centroCustoIdAnterior =
        filtrosAnteriores.centroCustoId || kanbanDataAnterior.centroCustoId
      yield call(getCentrosCusto)
      yield put(kanbanActions.setOptionsSelect([], 'pipelines'))
      yield put(kanbanActions.setFiltrosField('', 'pipelineId'))
      if (centroCustoIdAnterior) {
        yield call(getPipelines, {
          id: centroCustoIdAnterior,
          type: '',
          buscaPipelineDefault: false
        })
      } else {
        yield call(getPipelineDefault)
      }
    } else {
      yield put(kanbanActions.setOptionsSelect([], 'centrosCusto'))
      yield put(kanbanActions.setFiltrosField('', 'centroCustoId'))
      yield call(getPipelines, { id: '', type: '' })
    }
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getInboxesChatWoot(): SagaIterator {
  try {
    const response = yield call(kanbanController.getConexoesChatwoot)
    if (Array.isArray(response?.data?.content?.payload)) {
      yield put(
        kanbanActions.setInboxesChatWoot(response?.data?.content.payload)
      )
    }
  } catch (error) {
    handlerError(error)
  }
}

function* getAgentesChatwoot(): SagaIterator {
  try {
    const response = yield call(kanbanController.getAgentesChatwoot)
    if (Array.isArray(response?.data?.content)) {
      yield put(kanbanActions.setAgentesChatWoot(response?.data?.content))
    }
  } catch (error) {
    handlerError(error)
  }
}

function* getAgenteSelecionadoChatWoot(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(kanbanController.getAgenteSelecionado)

    if (response?.data?.content) {
      yield put(
        kanbanActions.setAgenteChatwootSelected(
          response?.data?.content.agenteId
        )
      )
      yield put(
        kanbanActions.setInboxChatwootSelected(response?.data?.content.inboxId)
      )
    }
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

interface EnviarMensagemChatwootProps extends Action {
  value: MensagemChatwootDto
}

function* enviarMensagemChatwoot({
  value
}: EnviarMensagemChatwootProps): SagaIterator {
  const { modalKanban }: KANBAN = yield select(
    (state: ApplicationState) => state.kanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    const formatedCpf = removeMaskCPF(value.cpfCliente)
    const response = yield call(kanbanController.enviarMensagemChatwoot, {
      mensagem: value.mensagem,
      agentId: value.agentId,
      inboxId: value.inboxId,
      cpfCliente: formatedCpf,
      numeroTelefone: value.numeroTelefone,
      pipelineEtapaClienteId: modalKanban.informacoes.pipelineEtapaClienteId
    })
    toast.success('Mensagem enviada com sucesso!')
    yield put(kanbanActions.cleanModalChatwoot())
    if (response?.data?.content?.url) {
      window.open(response?.data?.content?.url, '_blank')
    }
    yield call(getInformacoesCardKanban, {
      id: modalKanban.informacoes.pipelineEtapaClienteId,
      type: ''
    })
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getTiposAgendamento(): SagaIterator {
  try {
    yield put(kanbanActions.setLoadingSelect(true, 'tiposAgendamento'))
    const response = yield call(kanbanController.getTiposAgendamento)
    yield put(
      kanbanActions.setOptionsSelect(
        response?.data?.content || [],
        'tiposAgendamento'
      )
    )
  } catch (error) {
    yield put(kanbanActions.setOptionsSelect([], 'tiposAgendamento'))
    handlerError(error)
  } finally {
    yield put(kanbanActions.setLoadingSelect(false, 'tiposAgendamento'))
  }
}

function* criarAgendamento(): SagaIterator {
  const { agendamento }: KANBAN = yield select(
    (state: ApplicationState) => state.kanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    yield call(kanbanController.criarAgendamento, {
      pipelineEtapaClienteId: agendamento.pipelineEtapaClienteId,
      dataAtendimento: agendamento.dataAtendimento,
      tipoAtendimentoId: agendamento.tipoAgendamentoId,
      horario: agendamento.horario,
      observacao: agendamento.observacao,
      ddd: agendamento.ddd,
      telefone: agendamento.telefone
    })
    yield put(kanbanActions.cleanAgendamento())
    yield put(kanbanActions.setIsOpenModalAgendamento(false))
    toast.success('Agendamento criado com sucesso!')
    yield call(getInformacoesCardKanban, {
      id: agendamento.pipelineEtapaClienteId,
      type: ''
    })
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

interface DiscarClienteKanbanProps extends Action {
  value: {
    pipelineEtapaClienteId: string
    telefoneId: string
    numero: string
    cpf: string
  }
}

function* discarClienteKanban({
  value
}: DiscarClienteKanbanProps): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const cpfFormated = removeMaskCPF(value.cpf)
    const response = yield call(
      atendimentoController.verificaNaoPerturbe,
      cpfFormated
    )
    if (response?.data?.content) {
      toast.error(
        `Cliente com o CPF ${maskCPF(
          value.cpf
        )} registrado no não perturbe`,
        {
          className: 'text-break',
          autoClose: false
        }
      )
      return
    }
    yield put(
      kanbanActions.setModalDiscagem({
        isOpen: true,
        numeroDiscado: value.numero,
        cpfDiscando: cpfFormated,
        statusChamada: StatusChamadaEnum.discando
      })
    )
    yield call(kanbanController.discarCliente, {
      pipelineEtapaClienteId: value.pipelineEtapaClienteId,
      tipoLigacao: 1,
      telefoneId: value.telefoneId
    })
    toast.success('Ligação efetuada com sucesso!')
  } catch (error) {
    yield put(kanbanActions.cleanModalDiscagem())
    handlerError(error, 'Não foi possível realizar a ligação!')
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

interface AtualizarStatusDiscagemPorRetornoChamadaProps extends Action {
  field: string
  value: unknown
  cpf: string
}

function* atualizarStatusDiscagemPorRetornoChamada({
  field,
  value,
  cpf
}: AtualizarStatusDiscagemPorRetornoChamadaProps): SagaIterator {
  if (field !== 'statusChamada') return
  const { discagem, modalKanban }: KANBAN = yield select(
    (state: ApplicationState) => state.kanban
  )
  if (!discagem.isOpen || discagem.cpfDiscando !== cpf) return
  yield put(
    kanbanActions.setModalDiscagem({
      ...discagem,
      statusChamada: value as StatusChamadaEnum
    })
  )
  if (modalKanban.isOpen && modalKanban.informacoes.cpf === cpf) {
    yield call(getInformacoesCardKanban, {
      id: modalKanban.informacoes.pipelineEtapaClienteId,
      type: '',
      loading: false
    })
  }
}

function* verificaAtualizacaoKanban(
  notificacao: NotificacaoKanbanDto
): SagaIterator {
  const { modalKanban, kanbanData }: KANBAN = yield select(
    (state: ApplicationState) => state.kanban
  )
  try {
    if (
      modalKanban.isOpen &&
      notificacao.clienteEtapaPipelineId ===
        modalKanban.informacoes.pipelineEtapaClienteId
    ) {
      yield call(getInformacoesCardKanban, {
        id: modalKanban.informacoes.pipelineEtapaClienteId,
        type: '',
        loading: false
      })
    }
    if (notificacao.pipelineId === kanbanData.pipelineId) {
      yield call(getKanban, {
        loading: false,
        pipelineId: kanbanData.pipelineId
      })
    }
  } catch (error) {
    handlerError(error)
  }
}

function createHubChannelRetornoKanban(
  hubConnection: HubConnection,
  eventName: string
) {
  return eventChannel<NotificacaoKanbanDto>((emit) => {
    hubConnection.on(eventName, (body: NotificacaoKanbanDto) => {
      if (body) {
        emit(body)
      }
    })

    if (hubConnection.state === HubConnectionState.Disconnected) {
      hubConnection
        .start()
        .catch((err) => console.error('Erro ao iniciar a conexão:', err))
    }

    return () => {
      hubConnection
        .stop()
        .catch((err) => console.error('Erro ao parar a conexão:', err))
    }
  }, buffers.expanding<NotificacaoKanbanDto>(50))
}

function* initializeWebSocketsChannelRetornoKanban(): SagaIterator {
  try {
    const eventName = 'NotificaAtualizacaoKanban'
    const hubConnection: HubConnection = yield call(
      kanbanController.iniciarRetornoKanban,
      eventName
    )
    const channel = yield call(
      createHubChannelRetornoKanban,
      hubConnection,
      eventName
    )
    while (true) {
      const { notificacao, disconnect, atualizarInscricao } = yield race({
        notificacao: take(channel),
        disconnect: take(
          TYPES.DISCONNECT_WEB_SOCKETS_CHANNEL_RETORNO_KANBAN_SAGA
        ),
        atualizarInscricao: take(TYPES.ATUALIZAR_INSCRICAO_PIPELINE_KANBAN_SAGA)
      })
      if (disconnect) {
        channel.close()
        break
      }
      if (atualizarInscricao) {
        const { pipelineId, pipelineIdAnterior } = atualizarInscricao.payload
        if (pipelineIdAnterior) {
          yield call(
            [hubConnection, hubConnection.invoke],
            'DesinscreverPipeline',
            pipelineIdAnterior
          )
        }
        yield call(
          [hubConnection, hubConnection.invoke],
          'InscreverPipeline',
          pipelineId
        )
        continue
      }
      yield call(verificaAtualizacaoKanban, notificacao)
    }
  } catch (error) {
    console.error(error)
  }
}

function* watchGetKanban() {
  yield takeLatest(TYPES.GET_KANBAN_SAGA, getKanban)
}

function* watchGetCentrosCusto() {
  yield takeEvery(TYPES.GET_CENTROS_CUSTO_SAGA, getCentrosCusto)
}

function* watchGetPipelines() {
  yield takeEvery(TYPES.GET_PIPELINES_SAGA, getPipelines)
}

function* watchVerificarMotivoMovimentacao() {
  yield takeEvery(
    TYPES.VERIFICAR_MOTIVO_MOVIMENTACAO_SAGA,
    verificarMotivoMovimentacao
  )
}

function* watchExecutarMovimentacaoComMotivo() {
  yield takeEvery(
    TYPES.EXECUTAR_MOVIMENTACAO_COM_MOTIVO_SAGA,
    executarMovimentacaoComMotivo
  )
}

function* watchCriarMotivoMovimentacao() {
  yield takeEvery(TYPES.CRIAR_MOTIVO_MOVIMENTACAO_SAGA, criarMotivoMovimentacao)
}

function* watchGetMotivosPerda() {
  yield takeEvery(TYPES.GET_MOTIVOS_PERDA_SAGA, getMotivosPerda)
}

function* watchMovimentarCardKanbanSaga() {
  yield takeEvery(TYPES.MOVIMENTAR_CARD_KANBAN_SAGA, movimentarCardKanban)
}

function* watchGetInformacoesCardKanban() {
  yield takeEvery(
    TYPES.GET_INFORMACOES_CARD_KANBAN_SAGA,
    getInformacoesCardKanban
  )
}

function* watchAtribuirConsultorCardKanban() {
  yield takeEvery(
    TYPES.ATRIBUIR_CONSULTOR_CARD_KANBAN_SAGA,
    atribuirConsultorCardKanban
  )
}

function* watchGetConsultoresModalKanban() {
  yield takeEvery(
    TYPES.GET_CONSULTORES_MODAL_KANBAN_SAGA,
    getConsultoresModalKanban
  )
}

function* watchCriarAnotacaoCardKanban() {
  yield takeEvery(
    TYPES.CRIAR_ANOTACAO_CARD_KANBAN_SAGA,
    criarAnotacaoCardKanban
  )
}

function* watchGetBigNumbers() {
  yield takeEvery(TYPES.GET_BIG_NUMBERS_SAGA, getBigNumbers)
}

function* watchCadastrarCliente() {
  yield takeEvery(TYPES.CADASTRAR_CLIENTE_SAGA, cadastrarCliente)
}

function* watchAdicionarClienteKanban() {
  yield takeEvery(TYPES.ADICIONAR_CLIENTE_KANBAN_SAGA, adicionarClienteKanban)
}

function* watchGetConvenios() {
  yield takeEvery(TYPES.GET_CONVENIOS_SAGA, getConvenios)
}

function* watchGetPerfis() {
  yield takeEvery(TYPES.GET_PERFIS_SAGA, getPerfis)
}

function* watchGetPipelineDefault() {
  yield takeEvery(TYPES.GET_PIPELINE_DEFAULT_SAGA, getPipelineDefault)
}

function* watchVerificaHierarquiaEDisparaGets() {
  yield takeEvery(
    TYPES.VERIFICA_HIERARQUIA_E_DISPARA_GETS_SAGA,
    verificaHierarquiaEDisparaGets
  )
}

function* watchGetInboxesChatWoot() {
  yield takeEvery(TYPES.GET_INBOXES_CHATWOOT_SAGA, getInboxesChatWoot)
}

function* watchGetAgentesChatwoot() {
  yield takeEvery(TYPES.GET_AGENTES_CHATWOOT_SAGA, getAgentesChatwoot)
}

function* watchGetAgenteSelecionadoChatWoot() {
  yield takeEvery(
    TYPES.GET_AGENTE_SELECIONADO_CHATWOOT_SAGA,
    getAgenteSelecionadoChatWoot
  )
}

function* watchEnviarMensagemChatwoot() {
  yield takeEvery(TYPES.ENVIAR_MENSAGEM_CHATWOOT_SAGA, enviarMensagemChatwoot)
}

function* watchCriarAgendamento() {
  yield takeEvery(TYPES.CRIAR_AGENDAMENTO_SAGA, criarAgendamento)
}

function* watchGetTiposAgendamento() {
  yield takeEvery(TYPES.GET_TIPOS_AGENDAMENTO_SAGA, getTiposAgendamento)
}

function* watchInitializeWebSocketsChannelRetornoKanban() {
  yield takeEvery(
    TYPES.INITIALIZE_WEB_SOCKETS_CHANNEL_RETORNO_KANBAN_SAGA,
    initializeWebSocketsChannelRetornoKanban
  )
}

function* watchDiscarClienteKanban() {
  yield takeEvery(TYPES.DISCAR_CLIENTE_KANBAN_SAGA, discarClienteKanban)
}

function* watchAtualizarStatusDiscagemPorRetornoChamada() {
  yield takeEvery(
    ATENDIMENTO_TYPES.SET_INFOS_CHAMADA_CONTATO_BY_CPF_DISPATCH,
    atualizarStatusDiscagemPorRetornoChamada
  )
}

function* kanbanSaga() {
  yield all([
    fork(watchGetKanban),
    fork(watchGetCentrosCusto),
    fork(watchGetPipelines),
    fork(watchVerificarMotivoMovimentacao),
    fork(watchExecutarMovimentacaoComMotivo),
    fork(watchCriarMotivoMovimentacao),
    fork(watchGetMotivosPerda),
    fork(watchMovimentarCardKanbanSaga),
    fork(watchGetInformacoesCardKanban),
    fork(watchAtribuirConsultorCardKanban),
    fork(watchGetConsultoresModalKanban),
    fork(watchCriarAnotacaoCardKanban),
    fork(watchGetBigNumbers),
    fork(watchCadastrarCliente),
    fork(watchAdicionarClienteKanban),
    fork(watchGetConvenios),
    fork(watchGetPerfis),
    fork(watchGetPipelineDefault),
    fork(watchVerificaHierarquiaEDisparaGets),
    fork(watchGetInboxesChatWoot),
    fork(watchGetAgentesChatwoot),
    fork(watchGetAgenteSelecionadoChatWoot),
    fork(watchEnviarMensagemChatwoot),
    fork(watchCriarAgendamento),
    fork(watchGetTiposAgendamento),
    fork(watchInitializeWebSocketsChannelRetornoKanban),
    fork(watchDiscarClienteKanban),
    fork(watchAtualizarStatusDiscagemPorRetornoChamada)
  ])
}

export default kanbanSaga
